"use client";

import { type ReactNode, useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { DataGrid } from "@/components/tools/paper-formatting/data-grid";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import {
  type ApaTableModel,
  apaTableHtml,
  apaTableText,
  buildApaTable,
  type CellRun,
  cellRuns,
  parseTableData,
  probabilityNotes,
  rowsToTsv,
  splitNumber,
} from "@/lib/tool-kit/paper-formatting/apa-table";

/** English strings. A translated content file overrides these through `ui`. */
export const APA_TABLE_GENERATOR_STRINGS = {
  numberLabel: "Table number",
  titleLabel: "Table title",
  titlePlaceholder: "Descriptive statistics for study variables",
  titleCase: "Convert the title to APA title case",
  dataLabel: "Table data",
  dataPlaceholder: "Variable, _M_, _SD_\nAge (years), 21.4, 3.2",
  dataHint:
    "Paste cells from Excel or Google Sheets, or type comma-separated values. The first row is the column headings. Write _M_ for italics and ^a to mark a specific note.",
  editLabel: "Edit the data as",
  editModes: { text: "Text", grid: "Grid" },
  cellLabel: "Row {row}, column {column}",
  addRow: "Add row",
  addColumn: "Add column",
  removeRow: "Remove last row",
  removeColumn: "Remove last column",
  decimalAlign: "Align numbers on the decimal point",
  totalRow: "The last row holds totals (adds a rule above it)",
  generalNoteLabel: "General note",
  generalNotePlaceholder: "_M_ = mean; _SD_ = standard deviation.",
  specificNotesLabel: "Specific notes",
  specificNotesPlaceholder: "a Three participants skipped this item.",
  specificNotesHint:
    "One per line, starting with its letter. Mark the cell it belongs to with ^a, as in 209^a.",
  probabilityNote:
    "Add a probability note for the asterisks (*p < .05. **p < .01. ***p < .001.)",
  trySample: "Try a sample",
  clear: "Clear",
  sampleTitle:
    "descriptive statistics and correlations with grade point average",
  sampleData:
    "Variable\t_n_\t_M_\t_SD_\t_r_ with GPA\nSleep duration (hours)\t212\t6.84\t1.12\t.24**\nCaffeine (mg per day)\t212\t182.5\t96.3\t-.09\nScreen time (hours)\t209^a\t5.1\t2.47\t-.17*\nExercise (days per week)\t212\t2.9\t1.8\t.11",
  sampleGeneralNote: "GPA = grade point average.",
  sampleSpecificNotes: "a Three participants did not report screen time.",
  tableWord: "Table",
  noteWord: "Note",
  emptyPrompt: "Paste or type your data to build the table.",
  results: "Your APA table",
  previewLabel: "Preview of the APA table",
  copyRich: "Copy for Word or Google Docs",
  copyPlain: "Copy as plain text",
  copied: "Copied!",
  copyHint:
    "The rich copy is a real table with its rules set on the cells, so the borders, italics and decimal alignment survive the paste. Numbers line up because each numeric column is split at the decimal point into two cells under one heading.",
} as const;

type EditMode = "text" | "grid";

function runNode(run: CellRun, key: string): ReactNode {
  if (run.style === "sup") {
    return <sup key={key}>{run.text}</sup>;
  }
  if (run.style === "em") {
    return <em key={key}>{run.text}</em>;
  }
  return <span key={key}>{run.text}</span>;
}

function Cell({ text }: { text: string }) {
  // Runs are positional pieces of one cell's text; the offset makes each key unique.
  let offset = 0;
  return (
    <>
      {cellRuns(text).map((run) => {
        const key = `${offset}-${run.style}`;
        offset += run.text.length + 1;
        return runNode(run, key);
      })}
    </>
  );
}

const RULE = "border-foreground";

function BodyCells({
  model,
  row,
  edge,
}: {
  model: ApaTableModel;
  row: readonly string[];
  edge: string;
}) {
  return (
    <>
      {model.head.map((heading, column) => {
        const cell = row[column] ?? "";
        const key = `${column}-${heading}`;
        if (!model.split[column]) {
          const align = column === 0 ? "text-left" : "text-center";
          return (
            <td className={`px-2 py-1 align-top ${align} ${edge}`} key={key}>
              <Cell text={cell} />
            </td>
          );
        }
        const number = splitNumber(cell, model.decimalComma[column]);
        if (!number) {
          return (
            <td
              className={`px-2 py-1 text-center ${edge}`}
              colSpan={2}
              key={key}
            >
              <Cell text={cell} />
            </td>
          );
        }
        return [
          <td className={`py-1 pr-0 pl-2 text-right ${edge}`} key={`${key}-w`}>
            {number.whole}
          </td>,
          <td className={`py-1 pr-2 pl-0 text-left ${edge}`} key={`${key}-p`}>
            <Cell text={number.part} />
          </td>,
        ];
      })}
    </>
  );
}

function Notes({ model }: { model: ApaTableModel }) {
  return (
    <div className="mt-2 space-y-1 text-sm">
      {model.generalNote ? (
        <p>
          <em>{model.noteWord}.</em> <Cell text={model.generalNote} />
        </p>
      ) : null}
      {model.specificNotes.length > 0 ? (
        <p>
          {model.specificNotes.map((note) => (
            <span key={note.letter}>
              <sup>{note.letter}</sup> <Cell text={note.text} />{" "}
            </span>
          ))}
        </p>
      ) : null}
      {model.probabilityLevels > 0 ? (
        <p>
          {probabilityNotes(model.probabilityLevels).map((note) => (
            <span key={note.stars}>
              {note.stars}
              <em>p</em> &lt; {note.value}.{" "}
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}

function TablePreview({
  model,
  label,
}: {
  model: ApaTableModel;
  label: string;
}) {
  const last = model.body.length - 1;
  return (
    <section
      aria-label={label}
      className="overflow-x-auto rounded-lg border border-border bg-card p-4 font-serif text-foreground sm:p-6"
    >
      <p className="font-bold">{model.label}</p>
      <p className="mt-2 italic">{model.title}</p>
      <table className="mt-3 border-collapse text-sm">
        <thead>
          <tr>
            {model.head.map((cell, column) => (
              <th
                className={`border-y px-2 py-1 text-center font-normal ${RULE}`}
                colSpan={model.split[column] ? 2 : 1}
                key={`${column}-${cell}`}
                scope="col"
              >
                <Cell text={cell} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.body.map((row, index) => {
            const top =
              model.totalRow && index === last ? `border-t ${RULE}` : "";
            const bottom = index === last ? `border-b ${RULE}` : "";
            return (
              // Rows are positions in the table; they have no identity beyond it.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional rows
              <tr key={index}>
                <BodyCells edge={`${top} ${bottom}`} model={model} row={row} />
              </tr>
            );
          })}
        </tbody>
      </table>
      <Notes model={model} />
    </section>
  );
}

export default function ApaTableGeneratorWidget() {
  const strings = useToolUi(APA_TABLE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const [numberRaw, setNumberRaw] = useState("1");
  const [title, setTitle] = useState("");
  const [titleCase, setTitleCase] = useState(true);
  const [data, setData] = useState("");
  const [mode, setMode] = useState<EditMode>("text");
  const [decimalAlign, setDecimalAlign] = useState(true);
  const [totalRow, setTotalRow] = useState(false);
  const [generalNote, setGeneralNote] = useState("");
  const [specificNotes, setSpecificNotes] = useState("");
  const [probabilityNote, setProbabilityNote] = useState(true);
  const deferredData = useDeferredValue(data);
  // The grid edits the live rows so a cell never lags behind its own keystrokes; the
  // preview reads the deferred ones.
  const liveRows = useMemo(() => parseTableData(data), [data]);
  const rows = useMemo(() => parseTableData(deferredData), [deferredData]);
  const model = useMemo(
    () =>
      rows.length > 0
        ? buildApaTable(rows, {
            tableWord: strings.tableWord,
            noteWord: strings.noteWord,
            number: numberRaw,
            title,
            titleCase,
            locale,
            decimalAlign,
            totalRow,
            generalNote,
            specificNotes,
            probabilityNote,
          })
        : null,
    [
      rows,
      strings,
      numberRaw,
      title,
      titleCase,
      locale,
      decimalAlign,
      totalRow,
      generalNote,
      specificNotes,
      probabilityNote,
    ]
  );

  const loadSample = () => {
    setTitle(strings.sampleTitle);
    setData(strings.sampleData);
    setGeneralNote(strings.sampleGeneralNote);
    setSpecificNotes(strings.sampleSpecificNotes);
    setNumberRaw("1");
  };
  const clearAll = () => {
    setTitle("");
    setData("");
    setGeneralNote("");
    setSpecificNotes("");
  };

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[8rem_1fr]">
        <TextField
          id="apat-number"
          label={strings.numberLabel}
          onChange={setNumberRaw}
          value={numberRaw}
        />
        <TextField
          id="apat-title"
          label={strings.titleLabel}
          onChange={setTitle}
          placeholder={strings.titlePlaceholder}
          value={title}
        />
      </div>
      <Checkbox
        checked={titleCase}
        label={strings.titleCase}
        onChange={setTitleCase}
      />
      <PillGroup
        label={strings.editLabel}
        name="apat-mode"
        onChange={setMode}
        options={[
          { value: "text", label: strings.editModes.text },
          { value: "grid", label: strings.editModes.grid },
        ]}
        value={mode}
      />
      {mode === "text" ? (
        <TextAreaField
          hint={strings.dataHint}
          id="apat-data"
          label={strings.dataLabel}
          onChange={setData}
          placeholder={strings.dataPlaceholder}
          rows={8}
          value={data}
        />
      ) : (
        <DataGrid
          idPrefix="apat-cell"
          labels={{
            cell: strings.cellLabel,
            addRow: strings.addRow,
            addColumn: strings.addColumn,
            removeRow: strings.removeRow,
            removeColumn: strings.removeColumn,
          }}
          onChange={(next) => setData(rowsToTsv(next))}
          rows={liveRows}
        />
      )}
      <div className="space-y-2">
        <Checkbox
          checked={decimalAlign}
          label={strings.decimalAlign}
          onChange={setDecimalAlign}
        />
        <Checkbox
          checked={totalRow}
          label={strings.totalRow}
          onChange={setTotalRow}
        />
        <Checkbox
          checked={probabilityNote}
          label={strings.probabilityNote}
          onChange={setProbabilityNote}
        />
      </div>
      <TextField
        id="apat-general-note"
        label={strings.generalNoteLabel}
        onChange={setGeneralNote}
        placeholder={strings.generalNotePlaceholder}
        value={generalNote}
      />
      <TextAreaField
        hint={strings.specificNotesHint}
        id="apat-specific-notes"
        label={strings.specificNotesLabel}
        onChange={setSpecificNotes}
        placeholder={strings.specificNotesPlaceholder}
        prose
        rows={3}
        value={specificNotes}
      />
      <div className="flex flex-wrap gap-3">
        <button className={BUTTON_SECONDARY} onClick={loadSample} type="button">
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={data.length === 0 && title.length === 0}
          onClick={clearAll}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {model ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <TablePreview label={strings.previewLabel} model={model} />
          <div className="flex flex-wrap gap-3">
            <CopyButton
              copiedLabel={strings.copied}
              html={apaTableHtml(model)}
              label={strings.copyRich}
              text={apaTableText(model)}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyPlain}
              text={apaTableText(model)}
            />
          </div>
          <p className={HINT}>{strings.copyHint}</p>
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
